import React, { useState, useEffect, useContext, createContext } from "react"
import { Auth, Hub } from "aws-amplify"

// import { useCurrentUser } from "context/db"
import router from "next/router"
import PageLoader from "components/PageLoader"
import useLocalStorage from "hooks/useLocalStorage"

const authContext = createContext()

// Context Provider component that wraps your app and makes auth object
// available to any child component that calls the useAuth() hook.
export function AuthProvider({ children, ...props }) {
  const auth = useAuthProvider()
  return (
    <authContext.Provider value={auth} {...props}>
      {children}
    </authContext.Provider>
  )
}

// Hook that enables any component to subscribe to auth state
export const useAuth = () => {
  return useContext(authContext)
}

// Provider hook that creates auth object and handles state
function useAuthProvider() {
  // Store auth user object
  const [user, setUser] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(undefined)
  const [impersonation, setImpersonation] = useLocalStorage(
    "impersonation",
    undefined
  )

  // load up current user on page refresh or first load
  React.useEffect(() => {
    async function checkForLoggedInUser() {
      try {
        const user = await Auth.currentAuthenticatedUser()
        if (user) {
          setUser(user.attributes)
          setIsAuthenticated(true)
        }
      } catch (err) {
        setUser(false)
        setIsAuthenticated(false)
        console.warn(err)
      }
    }
    checkForLoggedInUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // set up a listener for changes in the auth state to update user and context
  React.useEffect(() => {
    Hub.listen("auth", data => {
      if (data.payload.event === "signOut") {
        setUser(false)
        setIsAuthenticated(false)
      }
      if (data.payload.event === "signIn") {
        setUser(
          data.payload.data.attributes ||
            data.payload.data.challengeParam.userAttributes
        )
        setIsAuthenticated(true)
      }
    })
  }, [])

  const signOut = async () => {
    setUser(false)
    setIsAuthenticated(false)
    await Auth.signOut()
    window.location = "/auth/signin"
    return
  }

  return {
    user,
    isAuthenticated,
    isImpersonating: !!impersonation?.id && !!impersonation?.email,
    impersonation,
    setImpersonation,
    signOut,
  }
}

// A Higher Order Component for requiring authentication
export const requireAuth = Component => {
  // eslint-disable-next-line
  return props => {
    // Get authenticated user
    const { user } = useAuth()

    useEffect(() => {
      // Redirect if not signed in
      if (user === false) {
        router.replace("/auth/signin")
      }
    }, [user])

    // Show loading indicator
    // We're either loading (user is null) or we're about to redirect (user is false)
    if (!user) {
      return <PageLoader />
    }

    // Render component now that we have user
    return <Component {...props} />
  }
}
